import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SignupModal = ({ isOpen, onClose }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formError, setFormError] = useState('');
  const modalRef = useRef(null);
  const successModalRef = useRef(null);

  // Form validation states
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    terms_conditions: '',
  });
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phoneNumber: false,
    terms_conditions: false,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    terms_conditions: false,
  });

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 3) return 'Name must be at least 3 characters';
    if (name.trim().length > 60) return 'Name cannot exceed 60 characters';
    if (!/^[a-zA-Z\s]+$/.test(name.trim()))
      return 'Name can only contain letters and spaces';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email address is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim()))
      return 'Please enter a valid email address';
    return '';
  };

  const validatePhoneNumber = (phone) => {
    if (!phone.trim()) return 'Phone number is required';
    return '';
  };

  const validateTerms = (accepted) => {
    if (!accepted)
      return 'You must accept the Terms of Service and Privacy Policy';
    return '';
  };

  // Validate individual field
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return validateName(value);
      case 'email':
        return validateEmail(value);
      case 'phoneNumber':
        return validatePhoneNumber(value);
      case 'terms_conditions':
        return validateTerms(value);
      default:
        return '';
    }
  };

  // Validate all fields
  const validateAllFields = () => {
    const errors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phoneNumber: validatePhoneNumber(formData.phoneNumber),
      terms_conditions: validateTerms(formData.terms_conditions),
    };

    setFieldErrors(errors);
    setTouchedFields({
      name: true,
      email: true,
      phoneNumber: true,
      terms_conditions: true,
    });

    return Object.values(errors).every((error) => error === '');
  };

  // Close modals when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        isOpen
      ) {
        handleClose();
      }

      if (
        successModalRef.current &&
        !successModalRef.current.contains(event.target) &&
        showSuccessMessage
      ) {
        closeSuccessModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, showSuccessMessage]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: fieldValue,
    });

    // Validate field if it has been touched
    if (touchedFields[name]) {
      const error = validateField(name, fieldValue);
      setFieldErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Mark field as touched
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate the field
    const error = validateField(name, fieldValue);
    setFieldErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateAllFields()) {
      setFormError('Please fix all errors before submitting');
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('terms_conditions', formData.terms_conditions);

      const response = await fetch(
        process.env.REACT_APP_BASE_URL + 'users/register-user',
        {
          method: 'POST',
          body: submitData,
        }
      );
      const data = await response.json();

      if (response.ok) {
        resetForm();
        handleClose();
        setShowSuccessMessage(true);
      } else {
        const errorMessage =
          data.message ||
          data.error ||
          'Registration failed. Please try again.';
        setFormError(errorMessage);
      }
    } catch (error) {
      setFormError('An unexpected error occurred. Please try again later.');
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessMessage(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      terms_conditions: false,
    });
    setFieldErrors({
      name: '',
      email: '',
      phoneNumber: '',
      terms_conditions: '',
    });
    setTouchedFields({
      name: false,
      email: false,
      phoneNumber: false,
      terms_conditions: false,
    });
    setFormError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Sign Up Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] backdrop-blur-sm">
        <div
          ref={modalRef}
          className="bg-[#121316] rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-auto scrollbar mx-4 "
        >
          <div className="px-8 pt-8  relative">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 bg-[#3B3C40] bg-opacity-80 rounded-full p-1.5 text-gray-300 hover:bg-opacity-100 transition-all shadow-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="mb-6 text-center">
              <h2 className="text-2xl font-bold text-[#ffffff] ">
                Join Spatial Grid
              </h2>
              <p className="text-[#989898]  mt-1">
                Create your account to get started
              </p>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-500 rounded-lg text-red-200">
                <div className="flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{formError}</span>
                </div>
              </div>
            )}
          </div>

          <div className="px-8 pt-0 pb-8 relative">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-5">
                {/* Full Name Field */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-[#ffffff] font-normal mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="Enter your full name"
                    className={`w-full px-4 py-2.5 rounded-lg focus:outline-none bg-[#3B3C40] text-gray-200 transition-all placeholder:text-[12px] placeholder:text-[#A6A6A6] ${
                      fieldErrors.name && touchedFields.name
                        ? 'border-2 border-[#EE2B2A] '
                        : 'border-2 border-transparent '
                    }`}
                  />
                  {fieldErrors.name && touchedFields.name && (
                    <p className="mt-1 text-sm text-[#EE2B2A] flex items-center">
                      {fieldErrors.name}
                    </p>
                  )}
                </div>

                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-[#ffffff] font-normal mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="Enter your email address"
                    className={`w-full px-4 py-2.5 rounded-lg focus:outline-none bg-[#3B3C40] text-gray-200 transition-all placeholder:text-[12px] placeholder:text-[#A6A6A6] ${
                      fieldErrors.email && touchedFields.email
                        ? 'border-2 border-[#EE2B2A] '
                        : 'border-2 border-transparent '
                    }`}
                  />
                  {fieldErrors.email && touchedFields.email && (
                    <p className="mt-1 text-sm text-[#EE2B2A] flex items-center">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                {/* Phone Number Field */}
                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-[#ffffff] font-normal mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="number"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="Enter your phone number"
                    className={`w-full px-4 py-2.5 rounded-lg focus:outline-none bg-[#3B3C40] text-gray-200 transition-all placeholder:text-[12px] placeholder:text-[#A6A6A6] ${
                      fieldErrors.phoneNumber && touchedFields.phoneNumber
                        ? 'border-2 border-[#EE2B2A] '
                        : 'border-2 border-transparent '
                    }`}
                    required
                  />
                  {fieldErrors.phoneNumber && touchedFields.phoneNumber && (
                    <p className="mt-1 text-sm text-[#EE2B2A] flex items-center">
                      {fieldErrors.phoneNumber}
                    </p>
                  )}
                </div>

                {/* Terms and Conditions */}
                <div className="pt-1">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      name="terms_conditions"
                      checked={formData.terms_conditions}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={`h-5 w-5 mt-0.5 mr-3 text-[#ffffff] border-gray-700 rounded bg-[#3B3C40] ${
                        fieldErrors.terms_conditions &&
                        touchedFields.terms_conditions
                          ? 'border-[#EE2B2A] '
                          : ''
                      }`}
                      required
                    />
                    <span className="text-sm text-[#ffffff]">
                      I agree to the{' '}
                      <a
                        href="/terms-conditions"
                        target="_blank"
                        onClick={handleClose}
                        className="text-[#EE2B2A] font-medium hover:underline"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="/privacy-policy"
                        onClick={handleClose}
                        target="_blank"
                        className="text-[#EE2B2A] font-medium hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                  {fieldErrors.terms_conditions &&
                    touchedFields.terms_conditions && (
                      <p className="mt-1 text-sm text-[#EE2B2A] flex items-center">
                        {fieldErrors.terms_conditions}
                      </p>
                    )}
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleClose}
                  className="mr-4 px-6 py-2.5 text-gray-400 font-medium hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-[#EE2B2A] text-white rounded-lg hover:bg-[#EE2B2A] transition-colors font-medium shadow-md text-nowrap focus:outline-none "
                >
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Message Modal */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] backdrop-blur-sm">
          <div
            ref={successModalRef}
            className="bg-[#1E1F23] rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-gray-700 p-6"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-200 mb-2">
                Registration Successful!
              </h3>
              <p className="text-gray-400">
                Your request has been received. Our team will verify and
                activate your account shortly. You will be notified once your
                account is activated.
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={closeSuccessModal}
                className="px-6 py-2.5 bg-[#EE2B2A] text-white rounded-lg hover:bg-[#EE2B2A] transition-colors font-medium shadow-md focus:outline-none focus:ring-2  focus:ring-offset-2"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupModal;
